// KeyPointVerticalComponent.js
import React from 'react';
import { Box, Typography } from '@mui/material';
import { linkProps } from '../../../utils/appLink';
import renderKeyPointText from './renderKeyPointText';
import ScrollFadeWrapper, {
  ScrollFadeSection,
} from '../../ScrollFadeWrapper/ScrollFadeWrapper';
import LinkWithIconComponent from '../LinkWithIcon/LinkWithIconComponent';
import { headingLetterSpacing } from '../../../utils/headingTypography';
import Image from '../../Image/Image';

// Cloudflare KeyPoint vector
const KEYPOINT_VECTOR_URL =
  'https://assets.askhippo.co.za/Vectors/KeyPointVerticalVector.svg';

const KeyPointVerticalComponent = ({ data }) => {
  const { title, description, footer, footerLink, steps, descriptionLink } =
    data;

  return (
    <ScrollFadeSection
      sx={{
        width: '100%',
        display: 'flex',
        justifyContent: 'center',
      }}
    >
      {/* prevent children from stretching vertically */}
      <Box
        sx={{
          position: 'relative',
          width: '1100px',
          display: 'flex',
          alignItems: 'flex-start',
        }}
      >
        {/* Sticky Blue Box */}
        <Box
          sx={{
            position: 'sticky',
            top: '120px',
            width: '390px',
            backgroundColor: '#162F3B',
            borderRadius: '64px',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'flex-start',
            alignItems: 'center',
            boxSizing: 'border-box',
            padding: '40px',
            zIndex: 2,
            flexShrink: 0,
            mt: '80px',
            mb: '80px',
            marginRight: '-80px',
            alignSelf: 'flex-start',
          }}
        >
          <Typography
            component="h2"
            sx={{
              fontFamily: 'Inter, sans-serif',
              fontWeight: 700,
              fontSize: '3rem',
              lineHeight: '3.5rem',
              letterSpacing: headingLetterSpacing('3rem'),
              color: '#FFFFFF',
              maxWidth: '310px',
              textAlign: 'center',
            }}
          >
            {title}
          </Typography>

          {description && (
            <Typography
              sx={{
                fontFamily: 'Inter, sans-serif',
                fontWeight: 400,
                fontSize: '1.5rem',
                lineHeight: '1.75rem',
                color: '#FFFFFF',
                mt: '32px',
                mb: '32px',
                textAlign: 'center',
                whiteSpace: 'pre-line',
              }}
            >
              {description
                ?.split(/(\*\*[^*]+\*\*|<G>[^<]+<G>)/g)
                .map((part, i) => {
                  if (/^\*\*[^*]+\*\*$/.test(part)) {
                    return (
                      <strong key={i} style={{ fontWeight: 700 }}>
                        {part.slice(2, -2)}
                      </strong>
                    );
                  } else if (/^<G>[^<]+<G>$/.test(part)) {
                    return (
                      <span key={i} style={{ color: '#C7CA00' }}>
                        <LinkWithIconComponent
                          {...linkProps(descriptionLink)}
                          showIcon={Boolean(descriptionLink)}
                          target="_blank"
                          iconSrc="https://assets.askhippo.co.za/Icons/common/Union.svg"
                          sx={{
                            color: '#C7CA00',
                            fontWeight: 500,
                          }}
                        >
                          {part.slice(3, -3)}
                        </LinkWithIconComponent>
                      </span>
                    );
                  } else {
                    return part;
                  }
                })}
            </Typography>
          )}

          {footer && (
            <LinkWithIconComponent
              href={footerLink || undefined}
              iconSrc="https://assets.askhippo.co.za/Icons/common/Union.svg"
              iconSize="0.75rem"
              sx={{
                mb: '15px',
                textAlign: 'center',
                fontFamily: 'Inter, sans-serif',
                fontWeight: 600,
                fontSize: '0.75rem',
                lineHeight: '1.25rem',
                letterSpacing: '-0.02em',
                color: '#FFFFFF',
              }}
            >
              {footer}
            </LinkWithIconComponent>
          )}
        </Box>

        {/* Scrollable White Container */}
        <Box
          sx={{
            width: '700px',
            backgroundColor: '#FFFFFF',
            borderRadius: '48px',
            paddingTop: '64px',
            paddingRight: '80px',
            paddingLeft: '104px',
            paddingBottom: '80px',
            boxSizing: 'border-box',
          }}
        >
          {steps.map(({ icon, text, title }, index) => (
            <ScrollFadeWrapper key={index} delay={index * 0.08}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'stretch',
                  mt: '16px',
                }}
              >
                {/* LEFT: icon + growing connector */}
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    alignSelf: 'stretch',
                  }}
                >
                  <Box
                    sx={{
                      position: 'relative',
                      width: '120px',
                      height: '120px',
                    }}
                  >
                    <Image
                      width={120}
                      height={120}
                      src={KEYPOINT_VECTOR_URL}
                      alt="Key point illustration (vertical)"
                      sx={{ width: '100%', height: '100%' }}
                    />
                    <Image
                      width={64}
                      height={64}
                      src={icon}
                      alt={steps[index].imgAltTag || `Step Icon ${index + 1}`}
                      title={
                        steps[index].imageAltTitle || `Step Icon ${index + 1}`
                      }
                      sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        width: '64px',
                        height: '64px',
                      }}
                    />
                  </Box>

                  {index !== steps.length - 1 && (
                    <Box
                      sx={{
                        width: '8px',
                        flexGrow: 1,
                        height: 'auto',
                        minHeight: '62px',
                        backgroundColor: '#E3E6E8',
                        borderRadius: '100px',
                        mt: '16px',
                      }}
                    />
                  )}
                </Box>

                {/* RIGHT: text */}
                <Box sx={{ maxWidth: '356px', textAlign: 'left', ml: '40px' }}>
                  <Typography
                    component="h3"
                    sx={{
                      fontFamily: 'Inter, sans-serif',
                      fontWeight: 700,
                      fontSize: '1.5rem',
                      lineHeight: '1.75rem',
                      letterSpacing: headingLetterSpacing('1.5rem'),
                      color: '#162F3B',
                      mb: '32px',
                    }}
                  >
                    {title}
                  </Typography>

                  {text && (
                    <Box>
                      {renderKeyPointText(text, {
                        paragraphSx: {
                          fontFamily: 'Inter, sans-serif',
                          fontWeight: 400,
                          fontSize: '1rem',
                          lineHeight: '1.5rem',
                          color: '#162F3B',
                        },
                        headingSx: {
                          fontFamily: 'Inter, sans-serif',
                          fontWeight: 700,
                          fontSize: '1rem',
                          lineHeight: '1.5rem',
                          color: '#162F3B',
                        },
                        listItemSx: {
                          fontFamily: 'Inter, sans-serif',
                          fontWeight: 400,
                          fontSize: '1rem',
                          lineHeight: '1.5rem',
                          color: '#162F3B',
                        },
                      })}
                    </Box>
                  )}
                </Box>
              </Box>
            </ScrollFadeWrapper>
          ))}
        </Box>
      </Box>
    </ScrollFadeSection>
  );
};

export default KeyPointVerticalComponent;
